import {
  Building2,
  ChevronsUpDown,
  Clock3,
  Globe,
  Keyboard,
  LogOut,
  MapPin,
  Monitor,
  Moon,
  PanelLeft,
  Sun,
  type LucideIcon,
} from 'lucide-react';
import {
  localTimeZone,
  setPreferences,
  usePreferences,
  type ClockChoice,
  type ZoneChoice,
} from '@/app/preferences';
import { ui } from '@/app/ui-store';
import { roleLabel } from '@/auth/abilities';
import { useSession } from '@/auth/session-context';
import { Avatar } from '@/components/ui/Avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/cn';
import { GLOW_OPTIONS, THEME_OPTIONS, useTheme, type ThemePreference } from '@/lib/theme';

const THEME_ICONS: Record<ThemePreference, LucideIcon> = {
  system: Monitor,
  light: Sun,
  'dark-sidebar': PanelLeft,
  dark: Moon,
};

/** A labelled row of the menu that holds a control rather than an action. */
function MenuRow({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-[2.125rem] items-center justify-between gap-3 px-2 text-fg-muted">
      <span id={id}>{label}</span>
      {children}
    </div>
  );
}

/**
 * Who is signed in, at the foot of the rail: the account row opens the
 * appearance (theme, ambient glow), time and sign-out menu. On the
 * collapsed rail it is the avatar.
 */
export function AccountMenu({ collapsed }: { collapsed: boolean }) {
  const { user, session, tenant, logout } = useSession();
  const { preference, glow, setPreference, setGlow, darkRail } = useTheme();
  const prefs = usePreferences(user.id);

  const trigger = (
    <button
      type="button"
      aria-label={`Account: ${user.name}. Open account menu`}
      className={cn(
        'flex items-center gap-2 rounded-md text-left text-base font-medium text-fg-muted outline-offset-0',
        'transition-[background-color,color] duration-[120ms] hover:bg-hover hover:text-fg data-[state=open]:bg-hover data-[state=open]:text-fg',
        collapsed ? 'w-full justify-center px-0 py-[0.3125rem]' : 'w-full px-2 py-[0.3125rem]',
      )}
    >
      <Avatar name={user.name} size={collapsed ? 'md' : 'sm'} inverted />
      {!collapsed && (
        <>
          <span className="min-w-0 flex-1 truncate text-fg">{user.name}</span>
          <ChevronsUpDown aria-hidden className="size-3.5 shrink-0 text-fg-subtle" strokeWidth={1.75} />
        </>
      )}
    </button>
  );

  return (
    <DropdownMenu>
      {collapsed ? (
        <Tooltip content={user.name} side="right">
          <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        </Tooltip>
      ) : (
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      )}
      <DropdownMenuContent
        side={collapsed ? 'right' : 'top'}
        align={collapsed ? 'end' : 'start'}
        sideOffset={collapsed ? 10 : 8}
        className={cn('w-[18.75rem] max-w-[calc(100vw-1rem)]', darkRail && 'dark-scope')}
      >
        <div className="flex items-center gap-2.5 p-2">
          <Avatar name={user.name} size="md" inverted />
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium text-fg">{user.name}</div>
            <div className="truncate text-xs text-fg-subtle">
              {user.email} · {roleLabel(session.role)}
            </div>
          </div>
        </div>

        <DropdownMenuSeparator />
        <DropdownMenuLabel>Appearance</DropdownMenuLabel>
        <MenuRow id="appearance-theme" label="Theme">
          <div
            role="group"
            aria-labelledby="appearance-theme"
            className="inline-flex gap-0.5 rounded-md border border-border-strong p-0.5"
          >
            {THEME_OPTIONS.map((option) => {
              const Icon = THEME_ICONS[option.value];
              const pressed = preference === option.value;
              return (
                <Tooltip key={option.value} content={option.label}>
                  <button
                    type="button"
                    aria-pressed={pressed}
                    aria-label={option.label}
                    onClick={() => setPreference(option.value)}
                    className={cn(
                      'grid h-[1.375rem] min-w-6 place-items-center rounded-sm px-2 text-xs font-medium text-fg-muted hover:text-fg',
                      pressed && 'bg-active text-fg',
                    )}
                  >
                    <Icon aria-hidden className="size-3.5" strokeWidth={1.5} />
                  </button>
                </Tooltip>
              );
            })}
          </div>
        </MenuRow>
        <MenuRow id="appearance-glow" label="Ambient glow">
          <div role="group" aria-labelledby="appearance-glow" className="flex items-center gap-2">
            {GLOW_OPTIONS.map((option) => {
              const pressed = glow === option.value;
              return (
                <Tooltip key={option.value} content={option.label}>
                  <button
                    type="button"
                    aria-pressed={pressed}
                    aria-label={`${option.label} glow`}
                    onClick={() => setGlow(option.value)}
                    style={option.swatch ? { background: option.swatch } : undefined}
                    className={cn(
                      'size-[1.125rem] rounded-full border border-border-strong',
                      !option.swatch &&
                        'bg-[linear-gradient(to_top_right,transparent_calc(50%-0.5px),var(--fg-subtle)_calc(50%-0.5px),var(--fg-subtle)_calc(50%+0.5px),transparent_calc(50%+0.5px))]',
                      pressed && 'outline outline-2 outline-offset-2 outline-fg',
                    )}
                  />
                </Tooltip>
              );
            })}
          </div>
        </MenuRow>

        <DropdownMenuSeparator />
        <DropdownMenuLabel>Times shown in</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={prefs.timeZone}
          onValueChange={(v) => setPreferences(user.id, { timeZone: v as ZoneChoice })}
        >
          <DropdownMenuRadioItem value="tenant">
            <Building2 aria-hidden strokeWidth={1.5} /> Tenant · {tenant.time_zone}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="local">
            <MapPin aria-hidden strokeWidth={1.5} /> Mine · {localTimeZone()}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="utc">
            <Globe aria-hidden strokeWidth={1.5} /> UTC
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuRadioGroup
          value={prefs.clock}
          onValueChange={(v) => setPreferences(user.id, { clock: v as ClockChoice })}
        >
          <DropdownMenuLabel>Clock</DropdownMenuLabel>
          <DropdownMenuRadioItem value="locale">
            <Clock3 aria-hidden strokeWidth={1.5} /> Locale default
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="24h">
            <Clock3 aria-hidden strokeWidth={1.5} /> 24-hour
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => ui.setShortcutsOpen(true)} shortcut="?">
          <Keyboard aria-hidden strokeWidth={1.5} /> Keyboard shortcuts
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => void logout()}
          className="text-danger data-[highlighted]:bg-[color-mix(in_srgb,var(--bp-danger)_10%,transparent)] data-[highlighted]:text-danger [&_svg]:text-danger"
        >
          <LogOut aria-hidden strokeWidth={1.5} /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
